/**
 * View service - Firestore CRUD operations for views
 * Path: users/{userId}/projects/{projectId}/views/{viewId}
 * Follows ARCH-003: Views nested under projects
 * Implements DATA-003: Token uniqueness global check
 */

import {
  collection,
  doc,
  updateDoc,
  setDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  collectionGroup,
  serverTimestamp,
  onSnapshot,
  writeBatch,
  deleteField,
  increment,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  View,
  TimerSettings,
  Photo,
  CameraSettings,
  CameraConfig,
  StatusHistoryDocument,
  TimerSettingsHistoryEntry,
  CapturingStateEntry,
} from '../types';
import { convertPhotoUrls } from '../utils/storage';
import { generateToken, isValidTokenFormat } from '../utils/token';
import { incrementViewCount, decrementViewCount } from './projectService';

/**
 * Move a view from one project to another.
 * Copies the view document to the new project path, deletes the old one,
 * and adjusts viewCount on both projects.
 */
export async function moveViewToProject(
  userId: string,
  oldProjectId: string,
  newProjectId: string,
  viewId: string
): Promise<void> {
  const oldRef = doc(db, `users/${userId}/projects/${oldProjectId}/views/${viewId}`);
  const newRef = doc(db, `users/${userId}/projects/${newProjectId}/views/${viewId}`);

  const snap = await getDoc(oldRef);
  if (!snap.exists()) throw new Error('View not found');

  const data = snap.data();
  const batch = writeBatch(db);

  batch.set(newRef, { ...data, projectId: newProjectId });
  batch.delete(oldRef);

  const oldProjectRef = doc(db, `users/${userId}/projects/${oldProjectId}`);
  const newProjectRef = doc(db, `users/${userId}/projects/${newProjectId}`);
  batch.update(oldProjectRef, { viewCount: increment(-1) });
  batch.update(newProjectRef, { viewCount: increment(1) });

  await batch.commit();
}

/**
 * Check if a token already exists globally (DATA-003)
 * Searches across ALL views in ALL users
 *
 * @private - Currently unused, will be enabled when Firebase index is ready
 */
// @ts-expect-error - Temporarily unused until Firebase index is ready
async function _tokenExists(token: string): Promise<boolean> {
  // Use collectionGroup to search all views across all users
  const viewsQuery = query(collectionGroup(db, 'views'), where('token', '==', token));

  const snapshot = await getDocs(viewsQuery);
  return !snapshot.empty;
}

/**
 * Generate a globally unique token
 * Retries until unique token found (DATA-003)
 *
 * TEMPORARY: Uniqueness check disabled until Firebase index is ready
 */
async function generateUniqueToken(): Promise<string> {
  // TEMPORARY: Skip uniqueness check due to pending Firebase index
  // TODO: Re-enable once single-field index for views/token is ready
  const token = generateToken();
  return token;

  /* Original implementation - re-enable when index is ready:
  let token = generateToken();
  let attempts = 0;
  const maxAttempts = 10;

  while (await _tokenExists(token)) {
    token = generateToken();
    attempts++;

    if (attempts >= maxAttempts) {
      throw new Error('Failed to generate unique token after 10 attempts');
    }
  }

  return token;
  */
}

/**
 * Create default camera settings
 */
function getDefaultCameraSettings(): CameraConfig {
  return {
    mode: 'internal',
    internal: {
      lens: 'wide',
      focusMode: 'auto',
      evCompensation: 0,
      hdrMode: 'off',
      jpegQuality: 'good',
      rawEnabled: false,
      gpsEnabled: false,
      lnmEnabled: true,
      lnmThreshold: 2.0,
      maxShutterSeconds: 1.0,
      saturationAdjust: 0,
      colorTempAdjust: 0,
    },
    dslr: { rawEnabled: false },
  };
}

/**
 * Count actual views across all projects by querying Firestore directly.
 * More reliable than usage.totalViews which is updated asynchronously by Cloud Functions.
 */
async function countActualViews(userId: string): Promise<number> {
  const projectsRef = collection(db, `users/${userId}/projects`);
  const projectsSnap = await getDocs(projectsRef);

  let total = 0;
  for (const projectDoc of projectsSnap.docs) {
    const viewsRef = collection(db, `users/${userId}/projects/${projectDoc.id}/views`);
    const viewsSnap = await getDocs(viewsRef);
    total += viewsSnap.size;
  }
  return total;
}

/**
 * Check if user can create more views based on plan limits.
 * Counts actual views in Firestore instead of relying on the async usage counter,
 * so it works correctly even right after deleting a view.
 */
export async function canCreateView(userId: string): Promise<{
  canCreate: boolean;
  currentViews: number;
  limit: number;
}> {
  // Get user document with plan and usage
  const userDoc = await getDoc(doc(db, 'users', userId));
  if (!userDoc.exists()) {
    return { canCreate: false, currentViews: 0, limit: 0 };
  }

  const userData = userDoc.data();

  // Count actual views from Firestore
  const currentViews = await countActualViews(userId);

  // Admin users bypass all limits
  if (userData?.isAdmin === true) {
    return { canCreate: true, currentViews, limit: Infinity };
  }

  const tier = userData?.plan?.tier || 'free';

  // Get plan config
  const plansDoc = await getDoc(doc(db, 'config', 'plans'));
  let viewsLimit = 3; // default free limit

  if (plansDoc.exists()) {
    const plansConfig = plansDoc.data();
    viewsLimit = plansConfig?.plans?.[tier]?.limits?.views || 3;
  }

  return {
    canCreate: currentViews < viewsLimit,
    currentViews,
    limit: viewsLimit,
  };
}

/**
 * Capture interval assigned to a freshly created view, in seconds.
 *
 * This is the value a new view is CONFIGURED with, written to disk at creation —
 * not a fallback applied when configuration is missing. Nothing anywhere may
 * substitute a value for absent settings: an absent config must stay visible as
 * absent. See devplans/TIMER-UNIFICATION.md (Q4).
 */
const INITIAL_TIMER_INTERVAL_SECONDS = 300;

/** Timer configuration written to every newly created view. */
function initialTimerSettings(): TimerSettings {
  return {
    interval: INITIAL_TIMER_INTERVAL_SECONDS,
    scheduleEnabled: false, // no time restriction: capture around the clock
  };
}

/**
 * Reject malformed timer settings before they reach Firestore.
 *
 * Six call sites build this object, three of them by spreading over a default of
 * their own; that is how three differently-shaped copies came to exist. This is the
 * single choke point for meaning — Firestore rules enforce the structural invariant
 * independently, for writers that never come through here.
 */
function assertValidTimerSettings(settings: TimerSettings): void {
  const { interval, scheduleEnabled, scheduleMode, dailySchedules, timeRanges, daysOfWeek } = settings;

  if (typeof interval !== 'number' || !Number.isFinite(interval)) {
    throw new Error('Timer settings: interval must be a number');
  }
  if (interval < MIN_TIMER_INTERVAL_SECONDS) {
    throw new Error(`Timer settings: interval must be at least ${MIN_TIMER_INTERVAL_SECONDS}s`);
  }
  if (typeof scheduleEnabled !== 'boolean') {
    throw new Error('Timer settings: scheduleEnabled must be a boolean');
  }

  if (!scheduleEnabled) return; // schedule fields are irrelevant when unrestricted

  if (scheduleMode === 'advanced') {
    if (!dailySchedules?.length) {
      throw new Error('Timer settings: advanced schedule requires at least one daily schedule');
    }
  } else {
    if (!daysOfWeek?.length) {
      throw new Error('Timer settings: a schedule requires at least one active day');
    }
    if (!timeRanges?.length) {
      throw new Error('Timer settings: a schedule requires at least one time range');
    }
  }

  const ranges = scheduleMode === 'advanced'
    ? (dailySchedules ?? []).flatMap((d) => d.timeRanges ?? [])
    : (timeRanges ?? []);
  for (const range of ranges) {
    if (toMinutes(range.start) >= toMinutes(range.end)) {
      throw new Error(`Timer settings: time range ${range.start}-${range.end} ends before it starts`);
    }
  }
}

/** Absolute floor for any interval. Stricter minimums (RAW, HDR) are enforced in the UI. */
const MIN_TIMER_INTERVAL_SECONDS = 10;

/** "HH:mm" to minutes since midnight; "24:00" is a valid end-of-day. */
function toMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/**
 * Create a new view with unique token
 * Throws error if user has reached view limit
 */
export async function createView(
  userId: string,
  projectId: string,
  data: {
    name: string;
  }
): Promise<{ viewId: string; token: string }> {
  // Check view limit before creating
  const { canCreate, currentViews, limit } = await canCreateView(userId);
  if (!canCreate) {
    throw new Error(`VIEW_LIMIT_REACHED:${currentViews}:${limit}`);
  }

  const viewsRef = collection(db, `users/${userId}/projects/${projectId}/views`);

  try {
    // Generate unique token (DATA-003)
    const token = await generateUniqueToken();

    const defaultCameraSettings = getDefaultCameraSettings();
    const timerSettings = initialTimerSettings();
    const viewData = {
      userId,
      projectId,
      name: data.name,
      token,
      createdAt: serverTimestamp(),
      capturing: false, // Capturing disabled by default
      // Camera settings: Android reads from settings.camera
      settings: {
        camera: defaultCameraSettings,
      },
      stats: {
        photoCount: 0,
        lastPhotoAt: null,
        storageMB: 0,
      },
    };

    // A view and its timer configuration are created together or not at all.
    // Creating them in separate writes is what produced views whose config existed
    // in one place only: the app read 5 min from the template while the dashboard,
    // finding no timerSettings, displayed a 1 min default of its own.
    // No timerTemplates mirror: a view created now can only ever be paired with a
    // current build, which reads its configuration from this document.
    const docRef = doc(viewsRef);
    await setDoc(docRef, { ...viewData, timerSettings });

    // Increment project view count
    await incrementViewCount(userId, projectId);

    return { viewId: docRef.id, token };
  } catch (error) {
    console.error('Error in createView:', error);
    throw error;
  }
}

/**
 * Get all views for a project
 */
export async function getViewsByProject(
  userId: string,
  projectId: string
): Promise<View[]> {
  const viewsRef = collection(db, `users/${userId}/projects/${projectId}/views`);
  const q = query(viewsRef, orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    viewId: doc.id,
    ...doc.data(),
  })) as View[];
}

/**
 * Subscribe to real-time updates for all views in a project
 * Returns unsubscribe function to stop listening
 */
export function subscribeToViews(
  userId: string,
  projectId: string,
  onUpdate: (views: View[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const viewsRef = collection(db, `users/${userId}/projects/${projectId}/views`);
  const q = query(viewsRef, orderBy('createdAt', 'desc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const views = snapshot.docs.map((doc) => ({
        viewId: doc.id,
        ...doc.data(),
      })) as View[];
      onUpdate(views);
    },
    (error) => {
      console.error('Error in views listener:', error);
      if (onError) {
        onError(error);
      }
    }
  );
}

/**
 * Get a single view by ID
 */
export async function getView(
  userId: string,
  projectId: string,
  viewId: string
): Promise<View | null> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
  const snapshot = await getDoc(viewRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    viewId: snapshot.id,
    ...snapshot.data(),
  } as View;
}

/**
 * Get view by viewId — resolves path via user's projects, then reads single doc.
 */
export async function getViewById(viewId: string, userId?: string): Promise<View | null> {
  if (userId) {
    const projectsSnap = await getDocs(collection(db, `users/${userId}/projects`));
    for (const projectDoc of projectsSnap.docs) {
      const viewRef = doc(db, `users/${userId}/projects/${projectDoc.id}/views/${viewId}`);
      const viewSnap = await getDoc(viewRef);
      if (viewSnap.exists()) {
        return { viewId: viewSnap.id, ...viewSnap.data() } as View;
      }
    }
    return null;
  }
  const viewsQuery = query(collectionGroup(db, 'views'));
  const snapshot = await getDocs(viewsQuery);
  const viewDoc = snapshot.docs.find(d => d.id === viewId);
  if (!viewDoc) return null;
  return { viewId: viewDoc.id, ...viewDoc.data() } as View;
}

/**
 * Subscribe to a single view — resolves path via user's projects,
 * then sets up a single-document listener.
 */
export function subscribeToView(
  viewId: string,
  onUpdate: (view: View | null) => void,
  onError?: (error: Error) => void,
  userId?: string
): Unsubscribe {
  let innerUnsub: Unsubscribe | null = null;
  let cancelled = false;

  const resolveAndSubscribe = async () => {
    try {
      let viewRef: ReturnType<typeof doc> | null = null;

      if (userId) {
        const projectsSnap = await getDocs(collection(db, `users/${userId}/projects`));
        for (const projectDoc of projectsSnap.docs) {
          if (cancelled) return;
          const ref = doc(db, `users/${userId}/projects/${projectDoc.id}/views/${viewId}`);
          const snap = await getDoc(ref);
          if (snap.exists()) {
            viewRef = ref;
            break;
          }
        }
      } else {
        const snapshot = await getDocs(query(collectionGroup(db, 'views')));
        const viewDoc = snapshot.docs.find(d => d.id === viewId);
        if (viewDoc) viewRef = viewDoc.ref;
      }

      if (cancelled) return;
      if (!viewRef) {
        onUpdate(null);
        return;
      }

      innerUnsub = onSnapshot(
        viewRef,
        (snap) => {
          if (snap.exists()) {
            onUpdate({ viewId: snap.id, ...snap.data() } as View);
          } else {
            onUpdate(null);
          }
        },
        (error) => {
          console.error('Error in view listener:', error);
          onError?.(error);
        }
      );
    } catch (error) {
      if (!cancelled) {
        console.error('Error resolving view path:', error);
        onError?.(error instanceof Error ? error : new Error(String(error)));
      }
    }
  };

  resolveAndSubscribe();

  return () => {
    cancelled = true;
    innerUnsub?.();
  };
}

/**
 * Find view by token (for Android authentication)
 * Searches globally across all users
 */
export async function findViewByToken(token: string): Promise<View | null> {
  if (!isValidTokenFormat(token)) {
    return null;
  }

  const viewsQuery = query(collectionGroup(db, 'views'), where('token', '==', token));
  const snapshot = await getDocs(viewsQuery);

  if (snapshot.empty) {
    return null;
  }

  const viewDoc = snapshot.docs[0];
  return {
    viewId: viewDoc.id,
    ...viewDoc.data(),
  } as View;
}

/**
 * Update timer settings for a view.
 *
 * Writes one document. The timerTemplates mirror this used to keep in step is gone:
 * every device reads views/{id}.timerSettings now. See devplans/TIMER-UNIFICATION.md.
 */
export async function updateTimerSettings(
  userId: string,
  projectId: string,
  viewId: string,
  capturing: boolean,
  timerSettings?: TimerSettings
): Promise<void> {
  if (timerSettings) {
    assertValidTimerSettings(timerSettings);
  }

  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);

  const viewUpdates: Record<string, unknown> = { capturing };
  if (timerSettings) {
    viewUpdates.timerSettings = timerSettings;
  }

  await updateDoc(viewRef, viewUpdates);
}

/**
 * Update camera settings for a view
 * Writes to settings.camera (Android reads from there)
 */
export async function updateCameraSettings(
  userId: string,
  projectId: string,
  viewId: string,
  cameraSettings: CameraSettings
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
  // Android reads from settings.camera
  // Use updateDoc to fully replace settings.camera (merge: true can cause issues with nested objects)
  await updateDoc(viewRef, {
    'settings.camera': cameraSettings,
  });
}

/**
 * Send restart command to all views (triggers OTA update on production installations).
 * Uses collectionGroup to reach views across all users/projects.
 */
export async function forceUpdateAllDevices(): Promise<number> {
  const viewsQuery = query(collectionGroup(db, 'views'));
  const snapshot = await getDocs(viewsQuery);

  const updates = snapshot.docs.map((viewDoc) => {
    return updateDoc(viewDoc.ref, {
      'commands.restart': {
        requestedAt: serverTimestamp(),
        status: 'pending',
      },
    });
  });

  await Promise.all(updates);
  return updates.length;
}

/**
 * Update per-view OTA auto-update setting
 */
export async function updateOtaAutoUpdate(
  userId: string,
  projectId: string,
  viewId: string,
  enabled: boolean
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
  await updateDoc(viewRef, { 'settings.otaAutoUpdate': enabled });
}

/**
 * Update view name
 */
export async function updateViewName(
  userId: string,
  projectId: string,
  viewId: string,
  name: string
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
  await updateDoc(viewRef, { name });
}

/**
 * Regenerate token for a view
 * Generates new unique token and updates view
 */
export async function regenerateToken(
  userId: string,
  projectId: string,
  viewId: string
): Promise<string> {
  const newToken = await generateUniqueToken();

  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
  await updateDoc(viewRef, {
    token: newToken,
    androidStatus: deleteField(),
  });

  return newToken;
}

/**
 * Delete a view
 * Note: Photo deletion will be handled by Cloud Function (DATA-002)
 */
export async function deleteView(
  userId: string,
  projectId: string,
  viewId: string
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
  await deleteDoc(viewRef);

  // Decrement project view count
  await decrementViewCount(userId, projectId);

  // Note: Cloud Function will handle cascade deletion of photos (DATA-002)
}

/**
 * Update view heartbeat (from Android device)
 */
export async function updateHeartbeat(
  userId: string,
  projectId: string,
  viewId: string,
  heartbeat: View['heartbeat']
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
  await updateDoc(viewRef, { heartbeat });
}

/**
 * Bulk update settings for all views in a project
 * Optionally sets capturing, interval, and/or rawEnabled for all views
 */
export async function updateBulkTimerSettings(
  userId: string,
  projectId: string,
  options: {
    capturing?: boolean;
    interval?: number;
    rawEnabled?: boolean;
  }
): Promise<void> {
  // Get all views for this project
  const views = await getViewsByProject(userId, projectId);

  // Update each view
  const updatePromises = views.map(async (view) => {
    const promises: Promise<void>[] = [];

    // Update timer settings if capturing or interval changed
    if (options.capturing !== undefined || options.interval !== undefined) {
      // Only ever adjust a configuration that exists. Synthesising one here would
      // put an interval nobody chose on a view nobody configured — the same mistake
      // that made the dashboard show a value the device never used.
      if (!view.timerSettings && options.interval !== undefined) {
        throw new Error(`View "${view.name}" has no timer configuration to update`);
      }

      const timerSettings = view.timerSettings
        ? { ...view.timerSettings, ...(options.interval !== undefined && { interval: options.interval }) }
        : undefined; // capturing-only change: takes updateTimerSettings' merge path

      // Determine capturing state
      const capturing = options.capturing !== undefined
        ? options.capturing
        : view.capturing;

      promises.push(updateTimerSettings(userId, projectId, view.viewId, capturing, timerSettings));
    }

    // Update camera settings if rawEnabled changed
    if (options.rawEnabled !== undefined) {
      // Update only rawEnabled, preserving other settings via merge
      const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${view.viewId}`);
      promises.push(
        setDoc(viewRef, {
          settings: {
            camera: {
              rawEnabled: options.rawEnabled,
            },
          },
        }, { merge: true })
      );
    }

    return Promise.all(promises);
  });

  await Promise.all(updatePromises);
}

/**
 * Subscribe to the latest photo for a view (real-time)
 * Returns the most recent photo by capturedAt timestamp
 */
export function subscribeToLatestPhoto(
  userId: string,
  viewId: string,
  onUpdate: (photo: Photo | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const photosQuery = query(
    collection(db, 'photos'),
    where('userId', '==', userId),
    where('viewId', '==', viewId),
    orderBy('capturedAt', 'desc'),
    limit(1)
  );

  return onSnapshot(
    photosQuery,
    (snapshot) => {
      if (snapshot.empty) {
        onUpdate(null);
        return;
      }

      const doc = snapshot.docs[0];
      const photoData = {
        ...doc.data(),
        photoId: doc.id,
      } as Photo;

      // Convert gs:// URLs to HTTPS
      const photo = convertPhotoUrls(photoData);
      onUpdate(photo);
    },
    (error) => {
      console.error('Error in latest photo listener:', error);
      if (onError) {
        onError(error);
      }
    }
  );
}

/**
 * Get status history for a specific date
 * Returns snapshots of device status throughout the day
 */
export async function getStatusHistory(
  userId: string,
  projectId: string,
  viewId: string,
  dateString: string
): Promise<StatusHistoryDocument | null> {
  const docRef = doc(
    db,
    `users/${userId}/projects/${projectId}/views/${viewId}/statusHistory/${dateString}`
  );
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data() as StatusHistoryDocument;
}

/**
 * Subscribe to status history for a specific date (real-time)
 */
export function subscribeToStatusHistory(
  userId: string,
  projectId: string,
  viewId: string,
  dateString: string,
  onUpdate: (history: StatusHistoryDocument | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const docRef = doc(
    db,
    `users/${userId}/projects/${projectId}/views/${viewId}/statusHistory/${dateString}`
  );

  return onSnapshot(
    docRef,
    (snapshot) => {
      if (snapshot.exists()) {
        onUpdate(snapshot.data() as StatusHistoryDocument);
      } else {
        onUpdate(null);
      }
    },
    (error) => {
      console.error('Error in status history listener:', error);
      if (onError) {
        onError(error);
      }
    }
  );
}

/**
 * Get timer settings history for a view
 * Returns all historical timer settings changes
 */
export async function getTimerSettingsHistory(
  userId: string,
  projectId: string,
  viewId: string,
  limitCount?: number
): Promise<TimerSettingsHistoryEntry[]> {
  const historyRef = collection(
    db,
    `users/${userId}/projects/${projectId}/views/${viewId}/timerSettingsHistory`
  );

  let q = query(historyRef, orderBy('changedAt', 'desc'));
  if (limitCount) {
    q = query(q, limit(limitCount));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => docSnap.data() as TimerSettingsHistoryEntry);
}

/**
 * Subscribe to timer settings history (real-time)
 */
export function subscribeToTimerSettingsHistory(
  userId: string,
  projectId: string,
  viewId: string,
  onUpdate: (history: TimerSettingsHistoryEntry[]) => void,
  onError?: (error: Error) => void,
  limitCount?: number
): Unsubscribe {
  const historyRef = collection(
    db,
    `users/${userId}/projects/${projectId}/views/${viewId}/timerSettingsHistory`
  );

  let q = query(historyRef, orderBy('changedAt', 'desc'));
  if (limitCount) {
    q = query(q, limit(limitCount));
  }

  return onSnapshot(
    q,
    (snapshot) => {
      const history = snapshot.docs.map((docSnap) => docSnap.data() as TimerSettingsHistoryEntry);
      onUpdate(history);
    },
    (error) => {
      console.error('Error in timer settings history listener:', error);
      if (onError) {
        onError(error);
      }
    }
  );
}

/**
 * Get capturing state history for a view
 * Returns all historical capturing state changes (start/stop toggles)
 */
export async function getCapturingStateHistory(
  userId: string,
  projectId: string,
  viewId: string,
  limitCount?: number
): Promise<CapturingStateEntry[]> {
  const historyRef = collection(
    db,
    `users/${userId}/projects/${projectId}/views/${viewId}/capturingStateHistory`
  );

  let q = query(historyRef, orderBy('changedAt', 'desc'));
  if (limitCount) {
    q = query(q, limit(limitCount));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => docSnap.data() as CapturingStateEntry);
}

/**
 * Subscribe to capturing state history (real-time)
 */
export function subscribeToCapturingStateHistory(
  userId: string,
  projectId: string,
  viewId: string,
  onUpdate: (history: CapturingStateEntry[]) => void,
  onError?: (error: Error) => void,
  limitCount?: number
): Unsubscribe {
  const historyRef = collection(
    db,
    `users/${userId}/projects/${projectId}/views/${viewId}/capturingStateHistory`
  );

  let q = query(historyRef, orderBy('changedAt', 'desc'));
  if (limitCount) {
    q = query(q, limit(limitCount));
  }

  return onSnapshot(
    q,
    (snapshot) => {
      const history = snapshot.docs.map((docSnap) => docSnap.data() as CapturingStateEntry);
      onUpdate(history);
    },
    (error) => {
      console.error('Error in capturing state history listener:', error);
      if (onError) {
        onError(error);
      }
    }
  );
}

/**
 * Send restart command to a view's Android device
 * This triggers the device to restart the app, allowing Play Store updates to install
 *
 * The device will:
 * 1. Acknowledge the command by updating status to "acknowledged"
 * 2. Schedule an alarm to restart the app
 * 3. Kill the current process
 * 4. Restart via AlarmReceiver after a short delay
 */
export async function sendRestartCommand(
  userId: string,
  projectId: string,
  viewId: string
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);

  await updateDoc(viewRef, {
    'commands.restart': {
      requestedAt: serverTimestamp(),
      status: 'pending',
    },
  });
}

/**
 * Request bandwidth test from Android device.
 * Android will capture a photo, measure upload time, and report if
 * the current interval is sustainable.
 */
export async function requestBandwidthTest(
  userId: string,
  projectId: string,
  viewId: string
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);

  await updateDoc(viewRef, {
    'commands.bandwidthTest': {
      requestedAt: serverTimestamp(),
    },
  });
}

/**
 * Request test shot from Android device.
 * Android will capture a photo using current camera settings.
 */
export async function requestTestShot(
  userId: string,
  projectId: string,
  viewId: string
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);

  await updateDoc(viewRef, {
    'commands.testShot': {
      requestedAt: serverTimestamp(),
      status: 'pending',
    },
  });
}

/**
 * Request device reboot from dashboard.
 * Android listens for commands.reboot changes and triggers DeviceAdmin reboot.
 */
export async function requestReboot(
  userId: string,
  projectId: string,
  viewId: string
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);

  await updateDoc(viewRef, {
    'commands.reboot': {
      requestedAt: serverTimestamp(),
    },
  });
}

/**
 * Send Test Suite command to Android (6-shot HDR/LNR comparison)
 */
export async function requestTestSuite(
  userId: string,
  projectId: string,
  viewId: string
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);

  await updateDoc(viewRef, {
    'commands.testSuite': {
      requestedAt: serverTimestamp(),
      status: 'pending',
      currentStep: 0,
      totalSteps: 6,
      results: [],
    },
  });
}

/**
 * Write a desired DSLR property value to Firestore.
 * Android will read and apply it, then clear the field.
 */
export async function updateDslrDesiredProperty(
  userId: string,
  projectId: string,
  viewId: string,
  property: string,
  value: number | string | boolean | null
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
  await updateDoc(viewRef, {
    [`settings.camera.dslr.${property}`]: value,
  });
}

/**
 * Send live view start/stop command to Android device.
 */
export async function sendLiveViewCommand(
  userId: string,
  projectId: string,
  viewId: string,
  action: 'start' | 'stop'
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
  await updateDoc(viewRef, {
    'commands.liveView': {
      requestedAt: serverTimestamp(),
      action,
    },
  });
}

/**
 * Send focus drive command to Android device.
 * mode='burst': Android executes N steps locally in a tight loop (~40ms apart).
 * direction: 'near' or 'far'
 * steps: number of PTP driveFocus() calls to execute
 * ±1 uses slow direction (fine), ±5/±15/±50 use fast direction (coarse).
 */
export async function sendFocusDriveCommand(
  userId: string,
  projectId: string,
  viewId: string,
  direction: 'near' | 'far',
  steps: number
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);

  await updateDoc(viewRef, {
    'commands.focusDrive': {
      requestedAt: serverTimestamp(),
      mode: 'burst',
      direction,
      steps,
    },
  });
}

/**
 * Request GPS location from Android device.
 * Writes a gpsRequest with unique requestId so Android can deduplicate.
 */
export async function requestGpsLocation(
  userId: string,
  projectId: string,
  viewId: string
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);

  await updateDoc(viewRef, {
    'gpsRequest': {
      requestId: crypto.randomUUID(),
      requestedAt: serverTimestamp(),
    },
    'gpsError': deleteField(),
  });
}

/**
 * Request GPS location from all views in a project (batch).
 */
export async function requestGpsLocationAll(
  userId: string,
  projectId: string,
  viewIds: string[]
): Promise<void> {
  const promises = viewIds.map((viewId) => {
    const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
    return updateDoc(viewRef, {
      'gpsRequest': {
        requestId: crypto.randomUUID(),
        requestedAt: serverTimestamp(),
      },
      'gpsError': deleteField(),
    });
  });

  await Promise.all(promises);
}

export async function sendMasterPowerCommand(
  userId: string,
  projectId: string,
  viewId: string,
  command: 'cycle_camera' | 'cycle_hub' | 'reboot' | 'hold_ch1_off' | 'hold_ch1_on' | 'hold_ch2_off' | 'hold_ch2_on'
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
  await updateDoc(viewRef, {
    'masterPower.pendingCommand': command,
  });
}

export async function setMasterPowerRecoveryPaused(
  userId: string,
  projectId: string,
  viewId: string,
  paused: boolean
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
  await updateDoc(viewRef, {
    'masterPower.recoveryPaused': paused,
  });
}

export async function sendMasterPowerFirmwareUpdate(
  userId: string,
  projectId: string,
  viewId: string,
  update: { url: string; version: string; sha256?: string }
): Promise<void> {
  const viewRef = doc(db, `users/${userId}/projects/${projectId}/views/${viewId}`);
  await updateDoc(viewRef, {
    'masterPower.pendingFirmwareUpdate': update,
    'masterPower.lastOtaResult': deleteField(),
  });
}
